import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { FacetTypeEnum } from "@/components/pagination";
import apollo from "@/helpers/apollo";
import { getUserHeadMeta } from "@/helpers/user-meta";
import Badges from "@/pages/user/badges";
import { SSR_BADGES_QUERY } from "@/pages/user/profile/graphql";
import type { BadgesVariablesType, UserDataType } from "@/pages/user/types";

const fetchBadges = createServerFn({ method: "GET" })
  .inputValidator((data: { category: string; username: string }) => data)
  .handler(async ({ data }) => {
    const client = apollo();
    const { data: userData } = await client.query<
      UserDataType,
      BadgesVariablesType
    >({
      query: SSR_BADGES_QUERY,
      variables: {
        categorySlugs: [data.category],
        isUser: false,
        username: data.username,
      },
    });

    const badges = userData.badges?.objects || [];

    if (!badges.length) {
      throw notFound();
    }

    const facets =
      (userData.badges?.facets || []).find(
        ({ type }) => type === FacetTypeEnum.CATEGORY
      )?.items || [];

    const selectedCategory = facets.find(({ slug }) => slug === data.category);

    return {
      badges,
      category: data.category,
      facets,
      title: selectedCategory ? `${selectedCategory.name} badges` : "Badges",
      user: userData.user,
    };
  });

export const Route = createFileRoute("/@{$username}/badges/$category")({
  component: BadgesCategoryPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    return {
      meta: [
        ...getUserHeadMeta(loaderData.user, "badges"),
        { content: "noindex", name: "robots" },
      ],
    };
  },
  loader: ({ params }) =>
    fetchBadges({
      data: { category: params.category, username: params.username },
    }),
});

function BadgesCategoryPage() {
  const data = Route.useLoaderData();
  if (!data) {
    return null;
  }
  return <Badges {...data} />;
}
